import type { PreviewEmployeeLineResponse, PreviewTaxLineResponse } from '../types';

function money(value: number): string {
  return value.toFixed(2);
}

function percent(rate: number | null): string {
  return rate === null ? '—' : `${Number((rate * 100).toFixed(3))}%`;
}

function LineTable({ title, lines, total }: { title: string; lines: PreviewTaxLineResponse[]; total: number }) {
  return (
    <div>
      <h5 style={{ margin: '8px 0 4px' }}>{title}</h5>
      {lines.length === 0 ? (
        <p className="muted">No {title.toLowerCase()} lines.</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Tax</th>
              <th>Rate</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={`${l.description}-${i}`}>
                <td>{l.description}</td>
                <td>{percent(l.rate)}</td>
                <td style={{ textAlign: 'right' }}>{money(l.amount)}</td>
              </tr>
            ))}
            <tr>
              <th scope="row" colSpan={2}>Total</th>
              <th style={{ textAlign: 'right' }}>{money(total)}</th>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}

/** The tax lines and deductions behind one employee's Payroll Preview totals (EPN-190), so a result can be checked line by
 * line against another calculator. Read-only; the numbers come from the saved calculation, nothing is recomputed here. */
export function TaxBreakdown({ employee }: { employee: PreviewEmployeeLineResponse }) {
  const lines = employee.taxLines ?? [];
  const employeeLines = lines.filter((l) => !l.isEmployerPaid);
  const employerLines = lines.filter((l) => l.isEmployerPaid);
  const pre = employee.preTaxDeductionsTotal ?? 0;
  const post = employee.postTaxDeductionsTotal ?? 0;
  const garnish = employee.garnishmentsTotal ?? 0;

  if (employee.payrollCalculationId === null) {
    return <p className="muted">No calculation was saved for this employee, so there is no breakdown.</p>;
  }

  return (
    <div>
      <h5 style={{ margin: '4px 0' }}>How net pay is reached</h5>
      <table>
        <tbody>
          <tr><td>Gross earnings</td><td style={{ textAlign: 'right' }}>{money(employee.grossEarnings)}</td></tr>
          <tr><td>Pre-tax deductions</td><td style={{ textAlign: 'right' }}>−{money(pre)}</td></tr>
          <tr><td>Taxable wages</td><td style={{ textAlign: 'right' }}>{money(employee.taxableWages ?? employee.grossEarnings)}</td></tr>
          <tr><td>Employee taxes</td><td style={{ textAlign: 'right' }}>−{money(employee.employeeTaxes)}</td></tr>
          <tr><td>Post-tax deductions</td><td style={{ textAlign: 'right' }}>−{money(post)}</td></tr>
          <tr><td>Garnishments</td><td style={{ textAlign: 'right' }}>−{money(garnish)}</td></tr>
          <tr><th scope="row">Net pay</th><th style={{ textAlign: 'right' }}>{money(employee.netPay)}</th></tr>
        </tbody>
      </table>
      <p className="muted" style={{ marginTop: 4 }}>
        Pre-tax deductions lower taxable wages but not the gross. Employer taxes are paid by the company and are not taken from net pay.
      </p>
      {lines.length === 0 ? (
        <p className="muted">This calculation has no tax lines to show.</p>
      ) : (
        <>
          <LineTable title="Employee taxes" lines={employeeLines} total={employee.employeeTaxes} />
          <LineTable title="Employer taxes" lines={employerLines} total={employee.employerTaxes} />
        </>
      )}
    </div>
  );
}
